import { assertCondition, assertNotNil } from '../../../../utils/types';
import { PathLink } from './pathLink';
import { IMergedNodeSummary } from '../interfaces';
import { OthersConfigAction } from '../../interfaces';

/**
 * Represents a node in the Path graph. The implementation uses adjacency set strategy
 * (https://en.wikipedia.org/wiki/Adjacency_list) to represent both incoming and outgoing neighbours as
 * the path graph is expected to be sparse than dense (links are only between nodes in sequential steps)
 * The key in the set is the key of the neighbour PathNode and the value is PathLink to capture weight
 *
 * Maintaining 2 sets (incoming and outgoing) requires more effort than just a single outgoing neighbours
 * but allows easy traversal in both directions, especially when performing merge operation or calculating
 * sums of link weights.
 *
 */
export class PathNode {
  private static sumLinkWeightsFunc = (sum: number, link: PathLink) =>
    sum + link.getWeight();

  public readonly nodeSeq: number;
  public readonly key: string;
  public readonly name: string;
  public readonly outgoingAdjacencySet: Record<string, PathLink> = {};
  public readonly incomingAdjacencySet: Record<string, PathLink> = {};
  public readonly mergedNodes: IMergedNodeSummary[] = [];
  public othersConfigAction: OthersConfigAction | undefined;

  constructor(key: string, name: string, nodeSeq: number) {
    this.key = key;
    this.name = name;
    this.nodeSeq = nodeSeq;
  }

  /**
   * Links this node with the node represented as destNode in the PathLink
   * If there is already a link between the 2 nodes, merge the links as implemented in PathLink.merge
   *
   * The operation modifies both this node outgoing links and link.destNode incoming links
   *
   * @param link - destination node in the link
   */
  public linkWith(link: PathLink) {
    const existingLink = this.getOutgoingLinkAsserted(link.getDestNode());
    if (existingLink) {
      existingLink.merge(link);
      return false;
    } else {
      this.addOutgoingLink(link);
      link.getDestNode().addIncomingLink(link);
      return true;
    }
  }

  /**
   * Destroys the link from this node to destNode. Opposite of linkWith. The link between the nodes must exist
   *
   * The operation modifies both this node outgoing links and destNode incoming links
   *
   * @param destNode - the node we want to unlink
   */
  public unlinkWith(destNode: PathNode) {
    const link = this.getOutgoingLinkAsserted(destNode);
    assertNotNil(link);
    this.removeOutgoingLink(link);
    link.getDestNode().removeIncomingLink(link);
  }

  /**
   * Merges the argument [node] into this node. All incoming links to the [node] become incoming links of this node
   * and all outgoing links become outgoing links of this node. A summary of the node properties is stored in this node
   * before linking for display purposes because the [node] becomes "disconnected" - no incoming or outgoing links
   *
   * The operation modifies both this node and the argument [node]
   *
   * @param node
   */
  public merge(node: PathNode) {
    assertCondition(node.nodeSeq === this.nodeSeq);
    const mergedNodeSummary: IMergedNodeSummary = {
      key: node.key,
      name: node.name,
      seq: node.nodeSeq,
      othersConfigAction: node.othersConfigAction,
      incomingCount:
        // first sequence is special: there are no incoming nodes. To avoid displaying 0s there,
        // we can use the outgoing instead
        node.nodeSeq === 0
          ? node.sumOfOutgoingWeights()
          : node.sumOfIncomingWeights(),
    };
    node.incomingAdjacencyList().forEach((link) => link.updateDestNode(this));
    node.outgoingAdjacencyList().forEach((link) => link.updateSourceNode(this));
    assertCondition(node.incomingAdjacencyList().length === 0);
    assertCondition(node.outgoingAdjacencyList().length === 0);
    this.mergedNodes.push(...node.mergedNodes, mergedNodeSummary);
  }

  /**
   * Are there any nodes that are merged into this one
   */
  public isMerged() {
    return this.mergedNodes.length > 0;
  }

  public sumOfIncomingWeights() {
    return this.incomingAdjacencyList().reduce(PathNode.sumLinkWeightsFunc, 0);
  }

  public sumOfIncomingWeightsFromMerged() {
    return this.incomingAdjacencyList().reduce(
      (sum, link) =>
        sum + (link.getSourceNode().isMerged() ? link.getWeight() : 0),
      0,
    );
  }

  public sumOfOutgoingWeights() {
    return this.outgoingAdjacencyList().reduce(PathNode.sumLinkWeightsFunc, 0);
  }

  public incomingAdjacencyList() {
    return Object.values(this.incomingAdjacencySet);
  }

  public outgoingAdjacencyList() {
    return Object.values(this.outgoingAdjacencySet);
  }

  public debug() {
    return {
      __type: 'PathNode',
      key: this.key,
      name: this.name,
      nodeSeq: this.nodeSeq,
      numMergedNodes: this.mergedNodes.length,
      incomingAdj: this.incomingAdjacencyList().map((link) => link.debug()),
      outgoingAdj: this.outgoingAdjacencyList().map((link) => link.debug()),
    };
  }

  private getOutgoingLinkAsserted(destNode: PathNode) {
    const existingLinkSource = this.getOutgoingLink(destNode);
    if (existingLinkSource) {
      // just for assert purpose
      const existingLinkDest = existingLinkSource
        .getDestNode()
        .getIncomingLink(this);
      assertCondition(existingLinkSource === existingLinkDest);
      return existingLinkSource;
    } else {
      return undefined;
    }
  }

  private getOutgoingLink(destNode: PathNode) {
    return this.outgoingAdjacencySet.hasOwnProperty(destNode.key)
      ? this.outgoingAdjacencySet[destNode.key]
      : undefined;
  }

  private getIncomingLink(sourceNode: PathNode) {
    return this.incomingAdjacencySet.hasOwnProperty(sourceNode.key)
      ? this.incomingAdjacencySet[sourceNode.key]
      : undefined;
  }

  private addIncomingLink(link: PathLink) {
    assertCondition(link.getDestNode() === this);
    assertCondition(
      !this.incomingAdjacencySet.hasOwnProperty(link.getSourceNode().key),
    );
    return (this.incomingAdjacencySet[link.getSourceNode().key] = link);
  }

  private removeIncomingLink(link: PathLink) {
    assertCondition(link.getDestNode() === this);
    assertCondition(
      this.incomingAdjacencySet.hasOwnProperty(link.getSourceNode().key),
    );
    return delete this.incomingAdjacencySet[link.getSourceNode().key];
  }

  private addOutgoingLink(link: PathLink) {
    assertCondition(link.getSourceNode() === this);
    assertCondition(
      !this.outgoingAdjacencySet.hasOwnProperty(link.getDestNode().key),
    );
    this.outgoingAdjacencySet[link.getDestNode().key] = link;
  }

  private removeOutgoingLink(link: PathLink) {
    assertCondition(link.getSourceNode() === this);
    assertCondition(
      this.outgoingAdjacencySet.hasOwnProperty(link.getDestNode().key),
    );
    return delete this.outgoingAdjacencySet[link.getDestNode().key];
  }
}
